/** V3.0.6 immersive onboarding wizard steps (single `/onboarding` URL). */
export type InsightWizardStep = "insight/1" | "insight/2" | "insight/3";

export type OnboardingWizardStep =
  | "score-intro"
  | "quiz/name"
  | "quiz/age"
  | "quiz/income"
  | "quiz/expenses"
  | "quiz/cash"
  | "quiz/debt"
  | "quiz/invest"
  | "quiz/risk"
  | "risk-profile-loading"
  | InsightWizardStep
  | "goal"
  | "loading"
  | "loading-error"
  | "fitness-score-result"
  | "result";

export const INSIGHT_STEPS: InsightWizardStep[] = [
  "insight/1",
  "insight/2",
  "insight/3",
];

export const isInsightWizardStep = (
  step: OnboardingWizardStep,
): step is InsightWizardStep =>
  step === "insight/1" || step === "insight/2" || step === "insight/3";

export const parseInsightIndex = (
  step: OnboardingWizardStep,
): 1 | 2 | 3 | null => {
  if (step === "insight/1") {
    return 1;
  }
  if (step === "insight/2") {
    return 2;
  }
  if (step === "insight/3") {
    return 3;
  }
  return null;
};

export type QuizProgressStep =
  | "quiz/name"
  | "quiz/age"
  | "quiz/income"
  | "quiz/expenses"
  | "quiz/cash"
  | "quiz/debt"
  | "quiz/invest"
  | "quiz/risk";

/** 组1 该上桌 / 组2 止血 / 组3 已在桌上 / 组4 存不下 / 组5 兜底 */
export type CohortGroup =
  | "ready_to_invest"
  | "stop_bleeding"
  | "already_investing"
  | "cant_save"
  | "fallback";

export type AgeBand = "A" | "B" | "C";

export type GoalId =
  | "money-leak"
  | "idle-cash"
  | "portfolio-risk"
  | "debt-free"
  | "million"
  | "home"
  | "retirement";

export type GoalResultVariant =
  | "money-leak"
  | "idle-cash"
  | "portfolio-risk"
  | "debt-free"
  | "million"
  | "home"
  | "retirement"
  | "runway";

export type SliderField = "income" | "expenses" | "cash" | "debt" | "invest";

export type SliderTouched = Record<SliderField, boolean>;

export type QuizAnswers = {
  name: string;
  /** 精确整数年龄（验收 P0-1：18–100，不再使用区间中位数） */
  age: number | null;
  income: number;
  expenses: number;
  cash: number;
  debt: number;
  invest: number;
  risk: string;
  goalId: string;
  sliderTouched: SliderTouched;
};

export const createEmptySliderTouched = (): SliderTouched => ({
  income: false,
  expenses: false,
  cash: false,
  debt: false,
  invest: false,
});

export const createAllTouchedSliderTouched = (): SliderTouched => ({
  income: true,
  expenses: true,
  cash: true,
  debt: true,
  invest: true,
});

export const QUIZ_PROGRESS_STEPS: QuizProgressStep[] = [
  "quiz/name",
  "quiz/age",
  "quiz/income",
  "quiz/expenses",
  "quiz/cash",
  "quiz/debt",
  "quiz/invest",
  "quiz/risk",
];

/** 需要完整问卷答案才能渲染的步骤（画像组）；恢复时答案不完整须回第一道未完成题。 */
export const PROFILE_ANSWER_STEPS: ReadonlySet<OnboardingWizardStep> = new Set([
  "risk-profile-loading",
  "insight/1",
  "insight/2",
  "insight/3",
  "goal",
]);

export const WIZARD_STEP_ORDER: OnboardingWizardStep[] = [
  "score-intro",
  ...QUIZ_PROGRESS_STEPS,
  "risk-profile-loading",
  ...INSIGHT_STEPS,
  "goal",
  "loading",
  "fitness-score-result",
  "result",
];

export interface OnboardingWizardStepHandlers {
  step: OnboardingWizardStep;
  answers: QuizAnswers;
  quizProgressIndex: number;
  quizProgressTotal: number;
  resultVariant: GoalResultVariant;
  displayName: string;
  goNext: (answersOverride?: QuizAnswers) => void;
  goBack: () => void;
  setAnswer: <K extends keyof QuizAnswers>(
    key: K,
    value: QuizAnswers[K],
  ) => void;
  setSliderField: (key: SliderField, value: number, touched: boolean) => void;
  retryLoading: () => void;
  completeLoading: () => void;
  failLoading: () => void;
  returnToGoal: () => void;
  simulateLoadingError?: boolean;
}
